import React, { useState, useEffect, useCallback, useMemo } from "react";

import { useRef } from "react";
import RoundedArrow from "@/components/icons/RoundedArrow";
import CreateAccount from "@/components/CreateAccount";
import Link from "@/assets/icons/Link.svg";
import Timestamp from "@/assets/icons/Timestamp.svg";
import Desktop from "@/assets/icons/Desktop.svg";
import Chrome from "@/assets/icons/Timestamp.svg";
import Apple from "@/assets/icons/Apple.svg";
import Anton from "@/assets/icons/Anton.svg";
import ThreeDots from "@/assets/icons/ThreeDots.svg";
import ThreeDotsHorizontal from "@/assets/icons/ThreeDotsHorizontal.svg";
import EditIcon from "@/assets/icons/Edit.svg";
import TrashRed from "@/assets/TrashRed.svg";
import GreySearch from "@/assets/icons/GreySearch.svg";
import ThreeDotsDropdown from "./ThreeDotsDropdown";
import Screenshot from "@/assets/icons/Screenshot.svg";
import TypingArrow from "@/assets/icons/TypingArrow.svg";
import Recording from "@/assets/icons/Recording.svg";
import Click from "@/assets/icons/Click.svg";
import { profilePictures } from "@/lib/data";
import { getTimePassed } from "@/lib/utils";
import { AnimatePresence, motion } from "motion/react";
import ResizableTable from "./ResizableTable";
import AntonStars from "@/assets/icons/AntonStars.svg";
import AntonStarsSmall from "@/assets/icons/AntonStarsSmall.svg";
import Messages from "@/assets/icons/Messages.svg";
import ConsoleLogs from "@/assets/icons/ConsoleLogs.svg";
import NetworkLogs from "@/assets/icons/NetworkLogs.svg";
import UserActions from "@/assets/icons/UserActions.svg";
import MicrophoneGrey from "@/components/icons/MicrophoneGrey";
import TimesRounded from "@/assets/icons/TimesRounded.svg";
import PlusMedia from "@/components/icons/PlusMedia";
import TimesSmMedia from "@/assets/icons/TimesSmMedia.svg";
import axios from "axios";
import { API_URL } from "@/env";
import Skeleton from "@mui/material/Skeleton";
import { type Report, type ConsoleLog } from "@/lib/types";
import useUserData from "@/hooks/useUserData";
import { useWindowSize } from "usehooks-ts";
import { Square, X } from "lucide-react";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import { AudioWaveform } from "@/components/AudioWaveform";
import { CommentAudioPlayer } from "@/components/CommentAudioPlayer";
import MediaSliderFull from "./MediaSliderFull";
import ImageChat from "@/assets/icons/ImageChat.svg";
import { useAICommentsStore } from "@/hooks/useAIComments";
// import CopyGrey from "@/assets/icons/CopyGrey.svg";
import CopySm from "@/assets/icons/CopySm.svg";
import { showCustomToast } from "../CustomToast";
import MCPModal from "./MCPModal";

const SIDEBAR_VERTICAL_DIVIDER_KEY = "sidebarVerticalDividerPosition";

const getInitialVerticalDividerPosition = () => {
  if (typeof window !== "undefined") {
    const savedPosition = localStorage.getItem(SIDEBAR_VERTICAL_DIVIDER_KEY);
    if (savedPosition) {
      const parsed = parseFloat(savedPosition);
      if (!isNaN(parsed) && parsed >= 20 && parsed <= 80) {
        return parsed;
      }
    }
  }
  return 50;
};

interface ChatMessage {
  id: string;
  text: string;
  timestamp: number;
  sender: string;
}

const CollapsibleObject: React.FC<{ data: any; depth?: number }> = ({
  data,
  depth = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (data === null) return <span className="text-orange-400">null</span>;
  if (data === undefined)
    return <span className="text-orange-400">undefined</span>;
  if (typeof data === "string")
    return <span className="text-emerald-400">"{data}"</span>;
  if (typeof data === "number")
    return <span className="text-cyan-400">{data}</span>;
  if (typeof data === "boolean")
    return <span className="text-pink-400">{data.toString()}</span>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-white/60">[]</span>;
    return (
      <span>
        <span
          className="cursor-pointer select-none text-white/60"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "▼" : "▶"} Array({data.length})
        </span>
        {isExpanded && (
          <div className="ml-4">
            {data.map((item, i) => (
              <div key={i} className="flex">
                <span className="text-white/60 mr-2">{i}:</span>
                <CollapsibleObject data={item} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </span>
    );
  }

  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (keys.length === 0) return <span className="text-white/60">{"{}"}</span>;
    return (
      <span>
        <span
          className="cursor-pointer select-none text-white/60"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "▼" : "▶"} {"{...}"}
        </span>
        {isExpanded && (
          <div className="ml-4">
            {keys.map((key) => (
              <div key={key} className="flex">
                <span className="text-violet-400 mr-2">{key}:</span>
                <CollapsibleObject data={data[key]} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </span>
    );
  }

  return <span className="text-gray-900">{String(data)}</span>;
};

const tryParseJSON = (str: string): { isJSON: boolean; data: any } => {
  const trimmed = str.trim();
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      return { isJSON: true, data: JSON.parse(trimmed) };
    } catch {
      return { isJSON: false, data: null };
    }
  }
  return { isJSON: false, data: null };
};

const ConsoleLogItem: React.FC<{ log: ConsoleLog }> = ({ log }) => {
  const { isJSON, data } = tryParseJSON(log.message);

  return (
    <div className={`px-[24px] py-[8px]`}>
      <div className="flex items-start gap-[12px]">
        {log?.timeOffset ? (
          <div className="text-xs leading-[16px] text-gray-800 shrink-0">
            {log.timeOffset}
          </div>
        ) : (
          <div className="text-xs leading-[16px] text-gray-800 shrink-0 opacity-0 cursor-default">
            0:00
          </div>
        )}
        <div className="break-all text-[12px] text-gray-900 leading-[16px]">
          {isJSON ? <CollapsibleObject data={data} /> : log.message}
        </div>
      </div>
    </div>
  );
};

const parseAICommentWithTimestamps = (text: string) => {
  // Split the text by timestamp markers
  const timestampRegex = /\[(\d{1,2}:\d{2})\]/g;
  const parts: Array<{ timestamp: string | null; content: string }> = [];

  // Check if there are any timestamps at all
  const hasTimestamps = timestampRegex.test(text);
  timestampRegex.lastIndex = 0;

  // If no timestamps, return the entire text as a single part
  if (!hasTimestamps) {
    if (text.trim()) {
      parts.push({ timestamp: null, content: text.trim() });
    }
    return parts;
  }

  let match;

  // Find first content before any timestamp
  const firstMatch = timestampRegex.exec(text);
  if (firstMatch && firstMatch.index > 0) {
    parts.push({
      timestamp: null,
      content: text.substring(0, firstMatch.index).trim(),
    });
  }

  // Reset regex
  timestampRegex.lastIndex = 0;

  while ((match = timestampRegex.exec(text)) !== null) {
    const timestamp = match[1];
    const startIdx = match.index + match[0].length;

    // Find next timestamp or end of string
    const nextMatch = timestampRegex.exec(text);
    const endIdx = nextMatch ? nextMatch.index : text.length;

    // Reset to continue from current position
    timestampRegex.lastIndex = match.index + match[0].length;

    const content = text.substring(startIdx, endIdx).trim();

    if (content) {
      parts.push({ timestamp, content });
    }
  }

  return parts;
};

// Helper function to render text with clickable links
const renderTextWithLinks = (text: string) => {
  // URL regex pattern
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "white",
            textDecoration: "underline",
            transition: "opacity 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.8";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

// Helper function to process text with markdown-like formatting AND links for dangerouslySetInnerHTML
const processTextWithLinksHTML = (text: string) => {
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.*?)`/g, "<code>$1</code>")
    .replace(/```(\w+)?\n([\s\S]*?)```/g, "<pre><code>$2</code></pre>")
    .replace(
      /(https?:\/\/[^\s]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: white; text-decoration: underline; transition: opacity 0.2s;" onmouseover="this.style.opacity=\'0.8\'" onmouseout="this.style.opacity=\'1\'">$1</a>',
    )
    .replace(/\n/g, "<br />");
};

const ReportSidebar: React.FC<{
  report: Report;
  onDividerPositionChange?: (position: number) => void;
}> = ({ report, onDividerPositionChange }) => {
  const [activeTab, setActiveTab] = useState("Info");
  const [isThreeDotsDropdownOpen, setIsThreeDotsDropdownOpen] = useState(false);
  const [isMCPModalOpen, setIsMCPModalOpen] = useState(false);
  const [consoleSearchTerm, setConsoleSearchTerm] = useState("");
  const [networkSearchTerm, setNetworkSearchTerm] = useState("");
  const [actionSearchTerm, setActionSearchTerm] = useState("");
  const [selectedConsoleLevels, setSelectedConsoleLevels] = useState<
    Set<string>
  >(new Set(["log", "error", "warn", "info", "debug"]));
  const [selectedNetworkStatuses, setSelectedNetworkStatuses] = useState<
    Set<string>
  >(new Set(["2xx", "3xx", "4xx", "5xx", "error"]));
  const [newMessage, setNewMessage] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  const { user } = useUserData();
  const { width } = useWindowSize();

  // Add state for divider position
  const [dividerPosition, setDividerPosition] = useState(
    getInitialVerticalDividerPosition,
  );
  const [isResizing, setIsResizing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const aiChatContainerRef = useRef<HTMLDivElement>(null);
  const threeDotsRef = useRef<HTMLDivElement>(null);
  const hasSummarized = useRef(false);

  const searchParams = new URLSearchParams(window.location.search);
  const reportId = searchParams.get("id");

  const chatTabs = ["ai", "comments"];
  const tabs = report?.isFromRecordingLink
    ? ["Info"]
    : [
        "Info",
        "Console",
        "Network",
        "Actions",
        "Sentry",
      ];

  // Handle mouse move during resize
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !sidebarRef.current) return;

      e.preventDefault();
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const newPosition =
        ((e.clientY - sidebarRect.top) / sidebarRect.height) * 100;

      // Limit the divider position to reasonable values (e.g., between 20% and 80%)
      const clampedPosition = Math.min(Math.max(newPosition, 20), 80);
      setDividerPosition(clampedPosition);
      onDividerPositionChange?.(clampedPosition);
    },
    [isResizing, onDividerPositionChange],
  );

  // Notify parent of initial divider position
  useEffect(() => {
    onDividerPositionChange?.(dividerPosition);
  }, []);

  // Handle outside click for three dots dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        threeDotsRef.current &&
        !threeDotsRef.current.contains(event.target as Node)
      ) {
        setIsThreeDotsDropdownOpen(false);
      }
    };

    if (isThreeDotsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isThreeDotsDropdownOpen]);

  const parseTimestampToSeconds = (timestamp: string): number => {
    const parts = timestamp.split(":");
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    return minutes * 60 + seconds;
  };

  const formatText = (text: string) => {
    return text
      .replace(
        /\[(\d{1,2}:\d{2})\]/g,
        '<span class="video-timestamp" data-time="$1" style="color: #FFCC00; font-weight: 500; font-size: 12px; margin-right: 8px; cursor: pointer; display: inline-flex; align-items: center; vertical-align: middle;">$1</span>',
      )
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/`(.*?)`/g, "<code>$1</code>")
      .replace(/```(\w+)?\n([\s\S]*?)```/g, "<pre><code>$2</code></pre>")
      .replace(/\n/g, "<br />");
  };

  // const formatText = (text: string) => {
  //   return text
  //     .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold text
  //     .replace(/`(.*?)`/g, "<code>$1</code>") // Inline code
  //     .replace(/```(\w+)?\n([\s\S]*?)```/g, "<pre><code>$2</code></pre>") // Code blocks
  //     .replace(/\n/g, "<br />"); // Line breaks
  // };

  // Handle mouse up to stop resizing
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = "auto";
    document.body.style.userSelect = "auto";
    // Save the current divider position to localStorage
    setDividerPosition((currentPosition) => {
      localStorage.setItem(
        SIDEBAR_VERTICAL_DIVIDER_KEY,
        currentPosition.toString(),
      );
      return currentPosition;
    });
  }, []);

  useEffect(() => {
    const handleTimestampClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("video-timestamp")) {
        const timeStr = target.getAttribute("data-time");
        if (timeStr) {
          const seconds = parseTimestampToSeconds(timeStr);
          window.dispatchEvent(
            new CustomEvent("seekToTimestamp", {
              detail: { seconds },
            }),
          );
        }
      }
    };

    document.addEventListener("click", handleTimestampClick);
    return () => document.removeEventListener("click", handleTimestampClick);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "auto";
      document.body.style.userSelect = "auto";
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter console logs
  const filteredConsoleLogs =
    report.consoleLogs?.filter((log) => {
      const matchesLevel = selectedConsoleLevels.has(log.level);
      const matchesSearch =
        consoleSearchTerm === "" ||
        log.message.toLowerCase().includes(consoleSearchTerm.toLowerCase()) ||
        (log.url &&
          log.url.toLowerCase().includes(consoleSearchTerm.toLowerCase()));
      return matchesLevel && matchesSearch;
    }) || [];

  const filteredNetworkLogs: any =
    (report.networkLogs || []).filter((log) => {
      try {
        const statusGroup =
          log?.status >= 200 && log?.status < 300
            ? "2xx"
            : log?.status >= 300 && log?.status < 400
              ? "3xx"
              : log?.status >= 400 && log?.status < 500
                ? "4xx"
                : log?.status >= 500
                  ? "5xx"
                  : "error";

        const matchesStatus = selectedNetworkStatuses.has(statusGroup);
        const matchesSearch =
          networkSearchTerm === "" ||
          (log?.url || "")
            .toLowerCase()
            .includes(networkSearchTerm.toLowerCase()) ||
          (log?.method || "")
            .toLowerCase()
            .includes(networkSearchTerm.toLowerCase()) ||
          (log?.statusText || "")
            .toLowerCase()
            .includes(networkSearchTerm.toLowerCase());

        return matchesStatus && matchesSearch;
      } catch (error) {
        console.error("Error filtering network log:", error, log);
        return false;
      }
    }) || [];

  const filteredActions =
    report.userActions?.filter((a: any) => {
      const search = actionSearchTerm.toLowerCase();
      if (!search) return true;

      return (
        a.type?.toLowerCase().includes(search) ||
        a.url?.toLowerCase().includes(search) ||
        a.target?.text?.toLowerCase().includes(search) ||
        a.target?.tagName?.toLowerCase().includes(search) ||
        a.timeOffset?.toLowerCase().includes(search)
      );
    }) || [];

  // Separate states for comments and AI chat
  const [commentText, setCommentText] = useState("");
  const [aiChatText, setAiChatText] = useState("");
  const [comments, setComments] = useState(report.comments);
  const [currentTab, setCurrentTab] = useState("ai");
  const [aiComments, setAIComments] = useState(report.aiComments);
  const { setAIComments: setGlobalAIComments } = useAICommentsStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isMicHovered, setIsMicHovered] = useState(false);
  const [activeAICommentMenu, setActiveAICommentMenu] = useState<number | null>(
    null,
  );
  const [editingAICommentIndex, setEditingAICommentIndex] = useState<
    number | null
  >(null);
  const [editingAICommentText, setEditingAICommentText] = useState("");

  // Sync local AI comments to global store
  useEffect(() => {
    setGlobalAIComments(aiComments);
  }, [aiComments, setGlobalAIComments]);

  // Voice recording (only for comments tab)
  const {
    isRecording,
    recordingTime,
    audioBlob,
    audioURL,
    startRecording,
    stopRecording,
    cancelRecording,
    clearRecording,
    analyserNode,
  } = useVoiceRecording();

  const [recordingDuration, setRecordingDuration] = useState(0);

  // Media upload state for comments - support multiple images
  const [uploadedImagesComment, setUploadedImagesComment] = useState<string[]>(
    [],
  ); // Local preview URLs (base64)
  const [uploadedImageUrlsComment, setUploadedImageUrlsComment] = useState<
    string[]
  >([]); // S3 URLs from server
  const [isUploadingImagesComment, setIsUploadingImagesComment] =
    useState(false);

  // Media upload state for AI - support multiple images
  const [uploadedImagesAI, setUploadedImagesAI] = useState<string[]>([]); // Local preview URLs (base64)
  const [uploadedImageUrlsAI, setUploadedImageUrlsAI] = useState<string[]>([]); // S3 URLs from server
  const [isUploadingImagesAI, setIsUploadingImagesAI] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageScrollRef = useRef<HTMLDivElement>(null);
  const [imageScrollPosition, setImageScrollPosition] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Helper values to get current tab's state
  const comment = useMemo(
    () => (currentTab === "ai" ? aiChatText : commentText),
    [currentTab, aiChatText, commentText],
  );
  const setComment = useMemo(
    () => (currentTab === "ai" ? setAiChatText : setCommentText),
    [currentTab],
  );
  const uploadedImages = useMemo(
    () => (currentTab === "ai" ? uploadedImagesAI : uploadedImagesComment),
    [currentTab, uploadedImagesAI, uploadedImagesComment],
  );
  const setUploadedImages = useMemo(
    () =>
      currentTab === "ai" ? setUploadedImagesAI : setUploadedImagesComment,
    [currentTab],
  );
  const uploadedImageUrls = useMemo(
    () =>
      currentTab === "ai" ? uploadedImageUrlsAI : uploadedImageUrlsComment,
    [currentTab, uploadedImageUrlsAI, uploadedImageUrlsComment],
  );
  const setUploadedImageUrls = useMemo(
    () =>
      currentTab === "ai"
        ? setUploadedImageUrlsAI
        : setUploadedImageUrlsComment,
    [currentTab],
  );
  const isUploadingImages = useMemo(
    () =>
      currentTab === "ai" ? isUploadingImagesAI : isUploadingImagesComment,
    [currentTab, isUploadingImagesAI, isUploadingImagesComment],
  );
  const setIsUploadingImages = useMemo(
    () =>
      currentTab === "ai"
        ? setIsUploadingImagesAI
        : setIsUploadingImagesComment,
    [currentTab],
  );

  // State for each comment's image carousel scroll
  const [commentScrollStates, setCommentScrollStates] = useState<{
    [key: string]: { canScrollLeft: boolean; canScrollRight: boolean };
  }>({});

  // State for MediaSliderFull
  const [showMediaSlider, setShowMediaSlider] = useState(false);
  const [mediaSliderImages, setMediaSliderImages] = useState<
    { mediaType: string; url: string }[]
  >([]);
  const [mediaSliderStartIndex, setMediaSliderStartIndex] = useState(0);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Drag and drop state
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Add global drag end listener to clear overlay if dropped outside
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      setIsDraggingOver(false);
    };

    const handleGlobalDrop = () => {
      setIsDraggingOver(false);
    };

    window.addEventListener("dragend", handleGlobalDragEnd);
    window.addEventListener("drop", handleGlobalDrop);

    return () => {
      window.removeEventListener("dragend", handleGlobalDragEnd);
      window.removeEventListener("drop", handleGlobalDrop);
    };
  }, []);

  useEffect(() => {
    const autoSummarize = async () => {
      if (
        report.mediaType === "video" &&
        report.media &&
        aiComments.length === 0 &&
        !hasSummarized.current
      ) {
        hasSummarized.current = true;
        try {
          setIsLoading(true);
          const summarizeResponse = await axios.post(
            `${API_URL}/video/summarize`,
            {
              videoUrl: report.media,
              reportId: report?._id,
            },
          );
          if (summarizeResponse?.data?.data) {
            setAIComments((prev: any) => [
              ...prev,
              {
                author: "ai",
                text: summarizeResponse.data.data.summary,
              },
            ]);
            setTimeout(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop =
                  chatContainerRef.current.scrollHeight;
              }
            });
          }
        } catch (err) {
        } finally {
          setIsLoading(false);
        }
      }
    };

    autoSummarize();
  }, [report.mediaType, report.media, report._id]);

  const handleTabClick = (tab: string) => {
    setCurrentTab(tab);
  };

  // Auto-scroll to bottom when AI is loading
  useEffect(() => {
    if (isLoading && chatContainerRef.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [isLoading]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "35px";
      if (textarea.scrollHeight > 35) {
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  };

  const handleCommentSubmit = async () => {
    if (!user?._id) {
      setShowAuthModal(true);
      return;
    }

    // Get the current image URLs directly from state based on tab
    const currentImageUrls =
      currentTab === "ai" ? uploadedImageUrlsAI : uploadedImageUrlsComment;

    if (!comment.trim() && currentImageUrls.length === 0) return;

    if (currentTab === "comments") {
      try {
        // Check if we have images (already uploaded to S3)
        if (currentImageUrls.length > 0) {
          const commentResponse = await fetch(`${API_URL}/reports/comment`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              reportId,
              text: comment.trim(),
              author: user._id,
              imageUrls: currentImageUrls,
            }),
          });

          if (!commentResponse.ok) {
            throw new Error("Failed to create comment");
          }

          const data = await commentResponse.json();

          if (data?._id) {
            setComment("");
            // Clear images - we're in comments tab here
            setUploadedImagesComment([]);
            setUploadedImageUrlsComment([]);
            setComments((prev) => [...prev, data]);
            setTimeout(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop =
                  chatContainerRef.current.scrollHeight;
              }
            });
            const textarea = textareaRef.current;
            if (textarea) {
              textarea.style.height = "35px";
            }
          }
        } else {
          // Regular text-only comment
          const response = await fetch(`${API_URL}/reports/comment`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              reportId,
              text: comment.trim(),
              author: user?._id,
            }),
          });

          const data = await response.json();

          if (data?._id) {
            setComment("");
            setComments((prev) => [...prev, data]);
            setTimeout(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop =
                  chatContainerRef.current.scrollHeight;
              }
            });
            const textarea = textareaRef.current;
            if (textarea) {
              textarea.style.height = "35px";
            }
          }
        }
      } catch (error) {
        console.error("Failed to add comment:", error);
        // Handle error (show toast, etc.)
      } finally {
        // setIsSubmitting(false);
      }
    } else {
      try {
        let commentText = comment;
        setComment("");
        const updateComments = [
          ...aiComments,
          {
            text: commentText.trim(),
            author: {
              ...user,
            },
          },
        ];
        setAIComments(updateComments);
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
        });
        setIsLoading(true);

        // Filter and optimize data before sending
        const optimizedNetworkLogs = report.networkLogs
          ? filterRelevantNetworkLogs(report.networkLogs)
          : [];

        const optimizedConsoleLogs = report.consoleLogs
          ? filterRelevantConsoleLogs(report.consoleLogs)
          : [];

        const optimizedUserActions = report.userActions
          ? filterRelevantUserActions(report.userActions)
          : [];

        // Trim device data to essentials only
        const optimizedDeviceData = report.info
          ? {
              url: report.info.url,
              browser: report.info.browser,
              os: report.info.os,
              windowSize: report.info.windowSize,
              country: report.info.country,
            }
          : {};

        const response = await fetch(`${API_URL}/reports/ai_comment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reportId,
            text: commentText.trim(),
            author: user?._id,
            networkLogs: optimizedNetworkLogs,
            consoleLogs: optimizedConsoleLogs,
            userActions: optimizedUserActions,
            deviceData: optimizedDeviceData,
          }),
        });

        const data = await response.json();

        setAIComments([
          ...updateComments,
          {
            author: "ai",
            text: data.aiResponse.content,
          },
        ]);
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
        });
      } catch (error) {
        console.error("Failed to add comment:", error);
        // Handle error (show toast, etc.)
      } finally {
        setIsLoading(false);
      }
    }
  };

  const truncateArray = <T,>(arr: T[], maxItems: number): T[] =>
    arr.slice(-maxItems); // Get last N items

  const truncateString = (str: string, maxLength: number): string =>
    str.length > maxLength
      ? str.substring(0, maxLength) + "...[truncated]"
      : str;

  const filterRelevantNetworkLogs = (logs: any[], maxLogs: number = 15) => {
    // Prioritize errors and failed requests
    const errors = logs.filter((log) => log.status >= 400 || log.error);
    const successful = logs.filter(
      (log) => log.status >= 200 && log.status < 400,
    );

    // Take last 10 errors + last 5 successful requests
    return [
      ...truncateArray(errors, 10),
      ...truncateArray(successful, 5),
    ].slice(-maxLogs);
  };

  // const filterRelevantConsoleLogs = (logs: string[], maxLogs: number = 20) => {
  //   // Prioritize errors and warnings
  //   const errors = logs.filter(
  //     (log) =>
  //       log.toLowerCase().includes("error") ||
  //       log.toLowerCase().includes("failed") ||
  //       log.toLowerCase().includes("exception")
  //   );
  //   const warnings = logs.filter((log) => log.toLowerCase().includes("warn"));
  //   const others = logs.filter(
  //     (log) =>
  //       !log.toLowerCase().includes("error") &&
  //       !log.toLowerCase().includes("warn")
  //   );

  //   return [
  //     ...truncateArray(errors, 10),
  //     ...truncateArray(warnings, 5),
  //     ...truncateArray(others, 5),
  //   ]
  //     .slice(-maxLogs)
  //     .map((log) => truncateString(log, 500));
  // };

  const filterRelevantConsoleLogs = (
    logs: (ConsoleLog | string)[],
    maxLogs: number = 20,
  ) => {
    // Convert to strings if needed
    const stringLogs = logs.map((log) =>
      typeof log === "string" ? log : log.message,
    );

    // Prioritize errors and warnings
    const errors = stringLogs.filter(
      (log) =>
        log.toLowerCase().includes("error") ||
        log.toLowerCase().includes("failed") ||
        log.toLowerCase().includes("exception"),
    );
    const warnings = stringLogs.filter((log) =>
      log.toLowerCase().includes("warn"),
    );
    const others = stringLogs.filter(
      (log) =>
        !log.toLowerCase().includes("error") &&
        !log.toLowerCase().includes("warn"),
    );

    return [
      ...truncateArray(errors, 10),
      ...truncateArray(warnings, 5),
      ...truncateArray(others, 5),
    ]
      .slice(-maxLogs)
      .map((log) => truncateString(log, 500));
  };

  const filterRelevantUserActions = (
    actions: any[],
    maxActions: number = 20,
  ) => {
    // Prioritize screenshots, errors, and navigation
    const screenshots = actions.filter((a) => a.type === "screenshot");
    const navigation = actions.filter((a) => a.type === "navigate");
    const interactions = actions.filter(
      (a) => a.type === "click" || a.type === "type",
    );

    return [
      ...truncateArray(screenshots, 5),
      ...truncateArray(navigation, 5),
      ...truncateArray(interactions, 10),
    ].slice(-maxActions);
  };

  // Voice recording handlers
  const handleMicClick = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      setIsMicHovered(false);
      await startRecording();
    }
  };

  const handleCancelRecording = () => {
    cancelRecording();
  };

  // Format recording time as MM:SS
  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Media upload handlers
  const handleMediaButtonClick = () => {
    fileInputRef.current?.click();
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   console.log('[Media] File change event', e.target.files);
  //   const files = e.target.files;
  //   if (!files || files.length === 0) {
  //     console.log('[Media] No files selected');
  //     return;
  //   }

  //   const newImages: string[] = [];
  //   let processedCount = 0;

  //   Array.from(files).forEach((file) => {
  //     if (file.type.startsWith('image/')) {
  //       console.log('[Media] Valid image file:', file.name, file.type);
  //       const reader = new FileReader();
  //       reader.onloadend = () => {
  //         newImages.push(reader.result as string);
  //         processedCount++;

  //         // When all files are processed, update state
  //         if (processedCount === files.length) {
  //           console.log('[Media] All images loaded, updating state with', newImages.length, 'images');
  //           setUploadedImages(prev => [...prev, ...newImages]);
  //         }
  //       };
  //       reader.readAsDataURL(file);
  //     } else {
  //       processedCount++;
  //     }
  //   });

  //   // Reset file input
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = '';
  //   }
  // };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }

    // Filter only image files
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/"),
    );

    if (imageFiles.length === 0) {
      return;
    }

    // Check total count won't exceed 10
    if (uploadedImages.length + imageFiles.length > 10) {
      alert(`Maximum 10 images allowed.`);
      return;
    }

    // Validate file sizes (max 10MB each)
    const maxSize = 10 * 1024 * 1024; // 10MB
    for (const file of imageFiles) {
      if (file.size > maxSize) {
        alert(`Image ${file.name} is too large (max 10MB per image)`);
        return;
      }
    }

    setIsUploadingImages(true);

    try {
      // Create local previews first for immediate UI feedback
      const newPreviews: string[] = [];
      for (const file of imageFiles) {
        const preview = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        newPreviews.push(preview);
      }

      // Update UI with previews immediately
      setUploadedImages((prev) => [...prev, ...newPreviews]);

      // Upload to S3 in the background
      const formData = new FormData();
      formData.append("reportId", reportId!);

      imageFiles.forEach((file) => {
        formData.append("images", file);
      });

      const response = await fetch(`${API_URL}/s3/upload-comment-images`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload images to S3");
      }

      const data = await response.json();

      // Store S3 URLs for later use when creating comment
      setUploadedImageUrls((prev) => [...prev, ...data.imageUrls]);
    } catch (error) {
      console.error("[Media] Failed to upload images:", error);
      // Remove the previews we added since upload failed
      setUploadedImages((prev) =>
        prev.slice(0, prev.length - imageFiles.length),
      );
      alert("Failed to upload images. Please try again.");
    } finally {
      setIsUploadingImages(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async (index: number) => {
    // Get the S3 URL to delete
    const imageUrlToDelete = uploadedImageUrls[index];

    if (imageUrlToDelete) {
      try {
        // Extract the S3 key from the full URL
        // URL format: https://cdn.cloudfront.net/comment-images/report-123/image-456-0.png
        // We need: comment-images/report-123/image-456-0.png
        const url = new URL(imageUrlToDelete);
        const key = url.pathname.substring(1); // Remove leading '/'

        // Call backend to delete the image from S3
        const response = await fetch(
          `${API_URL}/s3/delete/${encodeURIComponent(key)}`,
          {
            method: "DELETE",
          },
        );

        if (!response.ok) {
          console.error("[Media] Failed to delete image from S3");
          // Continue anyway to remove from UI
        }
      } catch (error) {
        console.error("[Media] Error deleting image from S3:", error);
        // Continue anyway to remove from UI
      }
    }

    // Remove from UI state - use tab-specific setters directly
    if (currentTab === "ai") {
      setUploadedImagesAI((prev) => prev.filter((_, i) => i !== index));
      setUploadedImageUrlsAI((prev) => prev.filter((_, i) => i !== index));
    } else {
      setUploadedImagesComment((prev) => prev.filter((_, i) => i !== index));
      setUploadedImageUrlsComment((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Update scroll button visibility
  const updateScrollButtons = () => {
    if (imageScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = imageScrollRef.current;
      setCanScrollLeft(scrollLeft > 1); // Show left button if scrolled at all
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Update scroll buttons for a specific comment's image carousel
  const updateCommentScrollButtons = (
    commentId: string,
    container: HTMLDivElement,
  ) => {
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCommentScrollStates((prev) => ({
      ...prev,
      [commentId]: {
        canScrollLeft: scrollLeft > 1,
        canScrollRight: scrollLeft < scrollWidth - clientWidth - 1,
      },
    }));
  };

  // Handler to open MediaSliderFull with images
  const handleOpenMediaSlider = (images: string[], startIndex: number) => {
    const slides = images.map((url) => ({ mediaType: "image", url }));
    setMediaSliderImages(slides);
    setMediaSliderStartIndex(startIndex);
    setShowMediaSlider(true);
  };

  const handleCloseMediaSlider = (
    currentTime?: number,
    currentIndex?: number,
  ) => {
    if (currentIndex !== undefined) {
      setCurrentMediaIndex(currentIndex);
    }
    setShowMediaSlider(false);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      currentTab === "comments" &&
      e.dataTransfer.items &&
      e.dataTransfer.items.length > 0
    ) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're truly leaving the container (not just entering a child element)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // If mouse is outside the container bounds, hide overlay
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    if (currentTab !== "comments") return;

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      return;
    }

    // Check total count won't exceed 10
    if (uploadedImages.length + imageFiles.length > 10) {
      alert(
        `Maximum 10 images allowed. You can add ${
          10 - uploadedImages.length
        } more.`,
      );
      return;
    }

    // Validate file sizes (max 10MB each)
    const maxSize = 10 * 1024 * 1024; // 10MB
    for (const file of imageFiles) {
      if (file.size > maxSize) {
        alert(`Image ${file.name} is too large (max 10MB per image)`);
        return;
      }
    }

    setIsUploadingImages(true);

    try {
      // Create local previews
      const newPreviews: string[] = [];
      for (const file of imageFiles) {
        const preview = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        newPreviews.push(preview);
      }

      // Update UI with previews immediately
      setUploadedImages((prev) => [...prev, ...newPreviews]);

      // Upload to S3
      const formData = new FormData();
      formData.append("reportId", reportId!);

      imageFiles.forEach((file) => {
        formData.append("images", file);
      });

      const response = await fetch(`${API_URL}/s3/upload-comment-images`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload images to S3");
      }

      const data = await response.json();

      // Store S3 URLs
      setUploadedImageUrls((prev) => [...prev, ...data.imageUrls]);
    } catch (error) {
      console.error("[DragDrop] Failed to upload images:", error);
      // Remove the previews we added since upload failed
      setUploadedImages((prev) =>
        prev.slice(0, prev.length - imageFiles.length),
      );
      alert("Failed to upload images. Please try again.");
    } finally {
      setIsUploadingImages(false);
    }
  };

  // Scroll images left
  const scrollImagesLeft = () => {
    if (imageScrollRef.current) {
      imageScrollRef.current.scrollBy({ left: -(65 + 6), behavior: "smooth" });
    }
  };

  // Scroll images right
  const scrollImagesRight = () => {
    if (imageScrollRef.current) {
      imageScrollRef.current.scrollBy({ left: 65 + 6, behavior: "smooth" });
    }
  };

  // Update scroll buttons when images change or scroll
  useEffect(() => {
    // Use a slight delay to ensure DOM is updated
    const timer = setTimeout(() => {
      updateScrollButtons();
    }, 100);
    return () => clearTimeout(timer);
  }, [uploadedImages]);

  useEffect(() => {
    const scrollContainer = imageScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", updateScrollButtons);
      // Initial check
      updateScrollButtons();
      return () =>
        scrollContainer.removeEventListener("scroll", updateScrollButtons);
    }
  }, [uploadedImages]);

  // Upload voice recording to backend
  const uploadVoiceRecording = async (blob: Blob, duration: number) => {
    if (!user?._id) {
      setShowAuthModal(true);
      return;
    }

    // Get current text value based on tab
    const textValue = currentTab === "ai" ? aiChatText : commentText;
    const setTextFn = currentTab === "ai" ? setAiChatText : setCommentText;

    // Clear recording UI immediately before showing optimistic comment
    clearRecording();
    setTextFn("");
    setRecordingDuration(0);

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "35px";
    }

    // Optimistic UI update - create temporary comment with blob URL
    const clientId = `client-${Date.now()}-${Math.random()}`;
    const tempId = `temp-${Date.now()}`;
    const blobUrl = URL.createObjectURL(blob);
    const optimisticComment = {
      _id: tempId,
      _clientId: clientId, // Stable ID that won't change
      text: textValue.trim(),
      author: {
        _id: user._id,
        username: user.username || user.email,
      },
      createdAt: new Date().toISOString(),
      hasAudio: true,
      audioUrl: blobUrl,
      audioDuration: duration,
      _tempBlobUrl: blobUrl, // Store blob URL so we can revoke it later
      _isOptimistic: true, // Flag to identify optimistic comments
    };

    // Add comment to UI immediately
    setComments((prev) => [...prev, optimisticComment]);

    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    });

    try {
      const formData = new FormData();
      formData.append("reportId", reportId!);
      formData.append("author", user._id);
      formData.append("text", optimisticComment.text);
      formData.append("audio", blob, "recording.webm");
      formData.append("duration", duration.toString());

      const response = await fetch(`${API_URL}/reports/comment/voice`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data?._id) {
        // Replace temporary comment with real one from server
        // Keep using the blob URL and preserve clientId to avoid remounting
        setComments((prev) =>
          prev.map((c) => {
            if (c._id === tempId) {
              // Use blob URL if it exists, otherwise use server URL
              const updated = {
                ...data,
                _clientId: clientId, // Keep stable client ID
                audioUrl: blobUrl || data.audioUrl,
                _keepBlobUrl: true,
              };
              return updated;
            }
            return c;
          }),
        );

        // Revoke blob URL after a delay to ensure playback isn't interrupted
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000); // Keep it for 1 minute
      }
    } catch (error) {
      console.error("Failed to upload voice recording:", error);
      // Revoke blob URL and remove optimistic comment on error
      URL.revokeObjectURL(blobUrl);
      setComments((prev) => prev.filter((c) => c._id !== tempId));
    }
  };

  // API Service Method
  async function summarizeUserActions(
    reportId: string,
    author: string,
    logs: Array<{
      timestamp: string;
      action: string;
      element?: string;
      value?: string;
      url?: string;
    }>,
  ) {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${API_URL}/reports/summarize-user-actions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reportId,
            author,
            logs,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to summarize user actions: ${response.statusText}`,
        );
      }

      const data = await response.json();

      setIsLoading(false);
      setAIComments((prev) => [
        ...prev,
        {
          author: "ai",
          text: data.aiResponse.content,
          isReproductionSteps: true,
        },
      ]);
      return data; // Returns { userComment, aiResponse, aiComment }
    } catch (err) {}
  }

  // Auto-trigger reproduction steps for dom-replay or image reports
  const hasTriggeredReproSteps = useRef(false);
  useEffect(() => {
    if (hasTriggeredReproSteps.current) return;

    const hasReproductionSteps = aiComments?.some(
      (comment: any) => comment.isReproductionSteps === true,
    );

    const shouldAutoGenerate =
      (report.mediaType === "dom-replay" || report.mediaType === "image") &&
      !hasReproductionSteps &&
      report.userActions?.length > 0;

    if (shouldAutoGenerate) {
      hasTriggeredReproSteps.current = true;
      summarizeUserActions(report._id, "ai", report.userActions);
    }
  }, [report._id, report.mediaType]);

  // Generate AI prompt for debugging
  const generateAIPrompt = useCallback(async () => {
    const sections: string[] = [];

    // Title section
    const title =
      report.title && report.title !== "Untitled"
        ? report.title
        : "Debug this:";
    sections.push(title);
    sections.push("");

    // URL context
    if (report.info?.url) {
      sections.push(`URL: ${report.info.url}`);
      sections.push("");
    }

    // Console logs (deduplicated)
    if (report.consoleLogs && report.consoleLogs.length > 0) {
      const seenMessages = new Set<string>();
      const uniqueLogs = report.consoleLogs.filter((log) => {
        const key = `${log.level}:${log.message}`;
        if (seenMessages.has(key)) return false;
        seenMessages.add(key);
        return true;
      });

      if (uniqueLogs.length > 0) {
        sections.push("## Console Logs");
        uniqueLogs.forEach((log) => {
          sections.push(`[${log.level.toUpperCase()}] ${log.message}`);
        });
        sections.push("");
      }
    }

    // Network requests (only errors - 4xx, 5xx, or error flag)
    if (report.networkLogs && report.networkLogs.length > 0) {
      const errorRequests = report.networkLogs.filter(
        (log) => log.status >= 400 || log.error,
      );

      if (errorRequests.length > 0) {
        sections.push("## Failed Network Requests");
        errorRequests.forEach((log) => {
          sections.push(`${log.method} ${log.url}`);
          sections.push(`  Status: ${log.status} ${log.statusText || ""}`);
          if (log.duration) {
            sections.push(`  Duration: ${log.duration}ms`);
          }
        });
        sections.push("");
      }
    }

    // Screenshot comments (CommentMarker)
    if (report.screenshotComments && report.screenshotComments.length > 0) {
      sections.push("## Screenshot Comments");
      report.screenshotComments.forEach((comment) => {
        const username = comment.username ? `${comment.username}: ` : "";
        sections.push(`- ${username}${comment.text}`);
      });
      sections.push("");
    }

    // Chat comments
    if (comments && comments.length > 0) {
      sections.push("## User Comments");
      comments.forEach((comment: any) => {
        if (comment.text) {
          sections.push(`- ${comment.text}`);
        }
      });
      sections.push("");
    }

    // AI chat comments (exclude reproduction steps entirely)
    if (aiComments && aiComments.length > 0) {
      const filteredAiComments = aiComments.filter((comment: any) => {
        if (!comment.text) return false;
        // Never include reproduction steps
        if (comment.isReproductionSteps) return false;
        return true;
      });

      if (filteredAiComments.length > 0) {
        sections.push("## AI Analysis");
        filteredAiComments.forEach((comment: any) => {
          if (comment.author === "ai" && comment.text) {
            sections.push(comment.text);
          } else if (comment.author === "user" && comment.text) {
            sections.push(`User: ${comment.text}`);
          }
        });
        sections.push("");
      }
    }

    const prompt = sections.join("\n").trim();

    try {
      await navigator.clipboard.writeText(prompt);
      // You could add a toast notification here if available
    } catch (err) {
      console.error("Failed to copy prompt to clipboard:", err);
    }

    return prompt;
  }, [report, comments, aiComments]);

  // Example Usage

  // Call the API

  // async function summarizeUserActions(
  //   logs: Array<{
  //     timestamp: string;
  //     action: string;
  //     element?: string;
  //     value?: string;
  //     url?: string;
  //   }>
  // ) {
  //   try {
  //     setIsLoading(true);
  //     const response = await fetch(`${API_URL}/openai/summarize-user-actions`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({ logs }),
  //     });

  //     if (!response.ok) {
  //       throw new Error(
  //         `Failed to summarize user actions: ${response.statusText}`
  //       );
  //     }

  //     const data = await response.json();
  //     setIsLoading(false);
  //     console.log({ data });
  //     setAIComments([
  //       {
  //         author: "ai",
  //         text: data.content,
  //       },
  //     ]);
  //     return data.content; // Returns the markdown summary
  //   } catch (err) {
  //     console.log(err);
  //   }
  // }

  // Handle audio blob when recording stops (only for comments tab)
  useEffect(() => {
    if (audioBlob && !isRecording && currentTab === "comments") {
      const duration =
        recordingTime > 0 ? recordingTime : recordingDuration || 1;
      setRecordingDuration(duration);
      uploadVoiceRecording(audioBlob, duration);
    }
  }, [audioBlob, isRecording]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [comment]);

  return (
    <>
      {showAuthModal && <CreateAccount setShowRegister={setShowAuthModal} />}
      <div
        ref={sidebarRef}
        className="w-full flex flex-col h-full gap-2 lg:gap-0"
      >
        {/* First section with dynamic height based on divider position */}
        <div
          className="flex relative flex-col justify-between bg-gray-100 rounded-[24px] overflow-hidden"
          style={{
            height: report.noTechnicalData
              ? "100%"
              : width > 1023
                ? `${dividerPosition}%`
                : "325px",
          }}
        >
          {/* Drag and Drop Overlay - Full Coverage */}
          {isDraggingOver && currentTab === "comments" && (
            <div className="absolute bg-black/[0.72] pointer-events-none backdrop-blur-[16px] inset-0 z-[9999] flex items-center justify-center rounded-[24px]">
              <div className="flex flex-col items-center gap-2">
                <img
                  src={ImageChat}
                  alt="Drop images"
                  className="w-[76px] h-[76px]"
                />
                <div className="text-center">
                  <p className="text-[16px] font-medium text-white leading-[24px] mb-1">
                    Drag and Drop Here
                  </p>
                  <p className="text-[14px] font-medium leading-[20px] text-gray-800">
                    PNG, JPG, JPEG or WEBP images,
                    <br />
                    up to 20 MB
                  </p>
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              borderRadius: "17px",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              padding: "4px",
              position: "absolute",
              top: 12,
              left: 12,
            }}
            className="w-[calc(100%-24px)] z-[10] backdrop-blur-[10px]"
          >
            <div
              style={{
                zIndex: 9,
                transition: "all 0.3s ease",
                position: "absolute",
                // width: "100%",
                borderRadius: "15px",
                top: "50%",
                height: 37,
                transform: "translateY(-50%)",
                left: currentTab === "ai" ? "4px" : "50%",
              }}
              className="w-[calc(50%-4px)] bg-gray-400"
            />
            {chatTabs.map((tab, i) => {
              return (
                <button
                  style={{
                    position: "relative",
                    textTransform: tab === "ai" ? "uppercase" : "capitalize",
                    color: "white",
                    fontSize: "14px",
                    fontFamily: "Onest",
                    lineHeight: "17.85px",
                    height: "37px",
                    width: "100%",
                    background: "transparent",
                    border: 0,
                  }}
                  key={i}
                  onClick={() => handleTabClick(tab)}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      fontWeight: "medium",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                      transition: "all 0.3s ease",
                      color: "white",
                    }}
                  >
                    {tab === "ai" && <img src={Anton} />}
                    {tab}
                  </div>
                </button>
              );
            })}
          </div>
          <div
            ref={chatContainerRef}
            className="h-full overflow-y-auto relative no-scrollbar"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {currentTab === "comments" && comments.length === 0 && (
              <div className="flex h-full w-[240px] mx-auto flex-col items-center justify-center">
                <img src={Messages} />
                <div className="text-center">
                  <p className="text-[16px] font-medium text-white leading-[24px]">
                    No Comments Right Now
                  </p>
                  <p className="text-[14px] font-medium leading-[20px] mt-1 text-gray-800">
                    Here will be all the comments from users.
                  </p>
                </div>
              </div>
            )}
            {currentTab === "ai" && aiComments.length === 0 && !isLoading && (
              <div className="flex h-full w-[240px] mx-auto flex-col items-center justify-center">
                <img src={AntonStars} />
                <div className="text-center font-medium">
                  <p className="text-[16px] font-medium text-white leading-[24px]">
                    Chat with AI
                  </p>
                  <p className="text-[14px] font-medium leading-[20px] mt-1 text-gray-800">
                    Ask him to explain, create tasks, make a summary regarding
                    this issue and much more.
                  </p>
                </div>
              </div>
            )}

            {currentTab === "ai" && aiComments.length > 0 && (
              <div className="flex flex-col">
                {aiComments.map((comment: any, i: number) => {
                  const isNewSender =
                    i === 0 ||
                    comment?.author?.username !==
                      aiComments[i - 1]?.author?.username ||
                    comment?.author !== aiComments[i - 1]?.author;

                  return (
                    <div
                      key={i}
                      className={`${
                        i === 0
                          ? "mt-[76px]"
                          : isNewSender
                            ? "mt-[20px]"
                            : "mt-[12px]"
                      } ${
                        i === aiComments.length - 1 && !isLoading
                          ? "mb-[100px]"
                          : "0"
                      } flex px-[20px] flex-col`}
                    >
                      {/* Author header - only show for new sender */}
                      {isNewSender && comment?.author !== "ai" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "1px",
                              backgroundColor: "rgba(255, 255, 255, 0.15)",
                              borderRadius: "50%",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={
                                profilePictures[
                                  //@ts-ignore
                                  comment?.author?.profilePicture - 1
                                ]
                              }
                              style={{
                                border: "0.5px solid rgba(255, 255, 255, 0.55)",
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                              }}
                            />
                          </div>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "white",
                              fontFamily: "Onest, sans-serif",
                              lineHeight: "16.58px",
                              marginBottom: 0,
                              flexShrink: 0,
                            }}
                          >
                            {comment?.author?.username}
                          </p>
                        </div>
                      )}

                      {isNewSender && comment?.author === "ai" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <img
                              className="w-[24px] h-[24px]"
                              src={AntonStarsSmall}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                color: "white",
                                fontFamily: "Onest, sans-serif",
                                lineHeight: "16.58px",
                                marginBottom: 0,
                                flexShrink: 0,
                              }}
                            >
                              AI
                            </p>
                          </div>
                          {comment?.isReproductionSteps && (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveAICommentMenu(
                                    activeAICommentMenu === i ? null : i,
                                  );
                                }}
                                className="p-1 rounded-md transition-colors"
                              >
                                <img
                                  src={ThreeDotsHorizontal}
                                  alt="Menu"
                                  className={`w-5 h-5 transition-all ${
                                    activeAICommentMenu === i
                                      ? "brightness-0 invert"
                                      : "opacity-50"
                                  }`}
                                />
                              </button>
                              <AnimatePresence>
                                {activeAICommentMenu === i && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute dropdown-shadow top-[calc(100%+4px)] text-[14px] leading-[20px] right-0 p-[4px] bg-gray-400 w-[200px] rounded-[16px] z-[100]"
                                  >
                                    <ul className="flex flex-col gap-1">
                                      <li
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingAICommentIndex(i);
                                          setEditingAICommentText(comment.text);
                                          setActiveAICommentMenu(null);
                                        }}
                                        className="flex font-medium justify-between hover:bg-gray-500 transition-all rounded-[12px] cursor-pointer items-center h-[36px] pl-[8px] pr-[12px]"
                                      >
                                        <div className="flex gap-1.5 items-center text-white">
                                          <img src={EditIcon} alt="Edit" />
                                          Edit repro steps
                                        </div>
                                      </li>
                                      <li
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const commentToDelete = aiComments[i];
                                          fetch(
                                            `${API_URL}/reports/ai_comment/${report._id}/${commentToDelete._id}`,
                                            {
                                              method: "DELETE",
                                            },
                                          );
                                          setAIComments((prev: any) =>
                                            prev.filter(
                                              (_: any, idx: number) =>
                                                idx !== i,
                                            ),
                                          );
                                          setActiveAICommentMenu(null);
                                        }}
                                        className="flex font-medium justify-between hover:bg-gray-500 transition-all rounded-[12px] cursor-pointer items-center h-[36px] pl-[8px] pr-[12px]"
                                      >
                                        <div className="flex gap-1.5 items-center text-[#DC265F]">
                                          <img src={TrashRed} alt="Delete" />
                                          Delete repro steps
                                        </div>
                                      </li>
                                    </ul>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Message content */}
                      {comment?.author === "ai" ? (
                        // AI comment with timestamp column layout
                        editingAICommentIndex === i ? (
                          // Edit mode
                          <div className="flex flex-col gap-2">
                            <textarea
                              value={editingAICommentText}
                              onChange={(e) =>
                                setEditingAICommentText(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  const commentToEdit = aiComments[i];
                                  fetch(`${API_URL}/reports/ai_comment`, {
                                    method: "PUT",
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({
                                      reportId: report._id,
                                      commentId: commentToEdit._id,
                                      text: editingAICommentText,
                                    }),
                                  });
                                  setAIComments((prev: any) =>
                                    prev.map((c: any, idx: number) =>
                                      idx === i
                                        ? { ...c, text: editingAICommentText }
                                        : c,
                                    ),
                                  );
                                  setEditingAICommentIndex(null);
                                  setEditingAICommentText("");
                                }
                                if (e.key === "Escape") {
                                  setEditingAICommentIndex(null);
                                  setEditingAICommentText("");
                                }
                              }}
                              ref={(el) => {
                                if (el) {
                                  el.focus();
                                  const len = el.value.length;
                                  el.setSelectionRange(len, len);
                                }
                              }}
                              className="w-full bg-transparent text-white text-[14px] font-[Onest,sans-serif] leading-[20px] border-none focus:outline-none resize-none p-0"
                              rows={Math.max(
                                3,
                                editingAICommentText.split("\n").length,
                              )}
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => {
                                  setEditingAICommentIndex(null);
                                  setEditingAICommentText("");
                                }}
                                className="px-3 py-1.5 text-[14px] h-[36px] font-medium leading-[20px] rounded-[12px] transition-all text-red-700 bg-red-alpha/[0.08] hover:bg-red-alpha/[0.16]"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => {
                                  const commentToEdit = aiComments[i];
                                  fetch(`${API_URL}/reports/ai_comment`, {
                                    method: "PUT",
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({
                                      reportId: report._id,
                                      commentId: commentToEdit._id,
                                      text: editingAICommentText,
                                    }),
                                  });
                                  setAIComments((prev: any) =>
                                    prev.map((c: any, idx: number) =>
                                      idx === i
                                        ? { ...c, text: editingAICommentText }
                                        : c,
                                    ),
                                  );
                                  setEditingAICommentIndex(null);
                                  setEditingAICommentText("");
                                }}
                                className="px-3 py-1.5 text-[14px] h-[36px] hover:bg-gray-400 font-medium leading-[20px] rounded-[12px] transition-all bg-gray-300"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className={`flex flex-col ${
                              /\[\d{1,2}:\d{2}\]/.test(comment.text)
                                ? "gap-[12px]"
                                : ""
                            }`}
                          >
                            {/* {comment.isReproductionSteps && (
                            <div
                              style={{
                                fontSize: "14px",
                                color: "white",
                                fontFamily: "Onest, sans-serif",
                                fontWeight: 600,
                                marginBottom: "8px",
                              }}
                            >
                              Reproduction steps:
                            </div>
                          )} */}
                            {parseAICommentWithTimestamps(comment.text).map(
                              (part, partIdx) => (
                                <div
                                  key={partIdx}
                                  className={`flex items-baseline ${
                                    part.timestamp
                                      ? "gap-[12px] cursor-pointer rounded-md -mx-2 px-2 py-1 -my-1"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (part.timestamp) {
                                      const seconds = parseTimestampToSeconds(
                                        part.timestamp,
                                      );
                                      window.dispatchEvent(
                                        new CustomEvent("seekToTimestamp", {
                                          detail: { seconds },
                                        }),
                                      );
                                    }
                                  }}
                                >
                                  {/* Timestamp column */}
                                  <div className="flex-shrink-0">
                                    {part.timestamp && (
                                      <span
                                        className="video-timestamp text-[#FFCC00] font-medium text-[14px] cursor-pointer"
                                        data-time={part.timestamp}
                                        style={{
                                          display: "inline-block",
                                          lineHeight: "20px",
                                        }}
                                      >
                                        {part.timestamp}
                                      </span>
                                    )}
                                  </div>

                                  {/* Content column */}
                                  <div
                                    className="flex-1"
                                    style={{
                                      wordBreak: "break-word",
                                      fontSize: "14px",
                                      fontFamily: "Onest, sans-serif",
                                      color: "white",
                                      lineHeight: "20px",
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: processTextWithLinksHTML(
                                        part.content,
                                      ),
                                    }}
                                  />
                                </div>
                              ),
                            )}
                          </div>
                        )
                      ) : (
                        // Regular user comment without timestamp parsing
                        <p
                          style={{
                            wordBreak: "break-word",
                            width: "100%",
                            fontSize: "14px",
                            fontFamily: "Onest, sans-serif",
                            color: "white",
                            lineHeight: "20px",
                          }}
                        >
                          {renderTextWithLinks(comment.text)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {currentTab === "ai" && isLoading && (
              <div
                className={`${
                  aiComments.length === 0 ? "mt-[76px]" : "mt-[20px]"
                } mb-[100px] flex px-[20px] flex-col`}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={24}
                    height={24}
                    sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                  />
                  <Skeleton
                    variant="text"
                    width={48}
                    height={16}
                    sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                  />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Skeleton
                    variant="text"
                    width="100%"
                    height={14}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      marginBottom: "8px",
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="85%"
                    height={14}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      marginBottom: "8px",
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="65%"
                    height={14}
                    sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                  />
                </div>
              </div>
            )}

            {currentTab === "comments" && (
              <div className="flex flex-col">
                {comments.map((comment: any, i) => {
                  return (
                    <div
                      key={comment._clientId || comment._id}
                      className={`${
                        i === comments.length - 1 ? "mb-[76px]" : "0"
                      } ${
                        i === 0
                          ? "mt-[76px]"
                          : comment?.author?.username !==
                              comments[i - 1]?.author?.username
                            ? "mt-[20px]"
                            : 0
                      } flex px-[20px] flex-col`}
                    >
                      {(i === 0 ||
                        comment?.author?.username !==
                          comments[i - 1]?.author?.username) && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "1px",
                              backgroundColor: "rgba(255, 255, 255, 0.15)",
                              borderRadius: "50%",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={
                                profilePictures[
                                  //@ts-ignore
                                  comment.author.profilePicture - 1
                                ]
                              }
                              style={{
                                border: "0.5px solid rgba(255, 255, 255, 0.55)",
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                              }}
                            />
                          </div>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "white",
                              fontFamily: "Onest, sans-serif",
                              lineHeight: "16.58px",
                              marginBottom: 0,
                              flexShrink: 0,
                            }}
                          >
                            {/*@ts-ignore */}
                            {comment?.author?.username}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "13px",
                                color: "rgba(255, 255, 255, 0.35)",
                                marginBottom: 0,
                              }}
                            >
                              {getTimePassed(comment.createdAt)}
                            </p>
                          </div>
                        </div>
                      )}
                      {comment.text && (
                        <p
                          style={{
                            wordBreak: "break-word",
                            width: "100%",
                            fontSize: "14px",
                            fontFamily: "Onest, sans-serif",
                            color: "white",
                            marginTop: 8,
                          }}
                        >
                          {renderTextWithLinks(comment.text)}
                        </p>
                      )}
                      {comment.hasImages &&
                        comment.imageUrls &&
                        comment.imageUrls.length > 0 && (
                          <div className="rounded-[14.6px] mt-2 relative -mx-[20px]">
                            {/* Left scroll button */}
                            <AnimatePresence>
                              {commentScrollStates[comment._id]
                                ?.canScrollLeft && (
                                <motion.button
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  type="button"
                                  onClick={() => {
                                    const container = document.getElementById(
                                      `comment-images-${comment._id}`,
                                    );
                                    if (container) {
                                      container.scrollBy({
                                        left: -(65 + 6),
                                        behavior: "smooth",
                                      });
                                    }
                                  }}
                                  className="absolute left-[20px] top-1/2 -translate-y-1/2 z-10 w-[24px] h-[24px] bg-gray-400/[0.32] backdrop-blur-[24px] rounded-[10px] flex items-center justify-center cursor-pointer transition-colors"
                                >
                                  <svg
                                    width="8"
                                    height="8"
                                    viewBox="0 0 8 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M5 1L2 4L5 7"
                                      stroke="white"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </motion.button>
                              )}
                            </AnimatePresence>

                            <div
                              id={`comment-images-${comment._id}`}
                              className="flex gap-[6px] overflow-x-auto no-scrollbar"
                              onScroll={(e) =>
                                updateCommentScrollButtons(
                                  comment._id,
                                  e.currentTarget,
                                )
                              }
                              ref={(el) => {
                                if (el) {
                                  // Initialize scroll state
                                  setTimeout(
                                    () =>
                                      updateCommentScrollButtons(
                                        comment._id,
                                        el,
                                      ),
                                    100,
                                  );
                                }
                              }}
                            >
                              {comment.imageUrls.map(
                                (imageUrl: string, imgIdx: number) => (
                                  <div
                                    key={imgIdx}
                                    className={`${
                                      imgIdx === 0
                                        ? "ml-[20px]"
                                        : imgIdx ===
                                            comment.imageUrls.length - 1
                                          ? "mr-[20px]"
                                          : ""
                                    } border-[1px] w-[65px] h-[65px] shrink-0 border-white/[0.07] rounded-[10px] overflow-hidden relative inline-block cursor-pointer`}
                                    onClick={() =>
                                      handleOpenMediaSlider(
                                        comment.imageUrls,
                                        imgIdx,
                                      )
                                    }
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={`Comment image ${imgIdx + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ),
                              )}
                            </div>

                            {/* Right scroll button */}
                            <AnimatePresence>
                              {commentScrollStates[comment._id]
                                ?.canScrollRight && (
                                <motion.button
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  type="button"
                                  onClick={() => {
                                    const container = document.getElementById(
                                      `comment-images-${comment._id}`,
                                    );
                                    if (container) {
                                      container.scrollBy({
                                        left: 65 + 6,
                                        behavior: "smooth",
                                      });
                                    }
                                  }}
                                  className="absolute right-[20px] top-1/2 -translate-y-1/2 z-10 w-[24px] h-[24px] bg-gray-400/[0.32] backdrop-blur-[24px] rounded-[10px] flex items-center justify-center cursor-pointer transition-colors"
                                >
                                  <svg
                                    width="8"
                                    height="8"
                                    viewBox="0 0 8 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M3 1L6 4L3 7"
                                      stroke="white"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </motion.button>
                              )}
                            </AnimatePresence>
                          </div>
                        )}
                      {comment.hasAudio && comment.audioUrl && (
                        <CommentAudioPlayer
                          key={comment._clientId || comment._id}
                          audioUrl={comment.audioUrl}
                          duration={comment.audioDuration || 0}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* {currentTab === "chat" && ( */}
          <div className="flex flex-col mt-2 absolute bottom-[12px] left-[12px] w-[calc(100%-24px)]">
            {/* {currentTab === "ai" && (
              <div className="mb-2 flex items-center gap-2">
                <button
                  onClick={() => {
                    generateAIPrompt();
                    // summarizeUserActions(report._id, "ai", filteredActions);
                  }}
                  disabled={isLoading}
                  className="py-1.5 px-3 bg-gray-300 rounded-[999px] text-[14px] font-medium disabled:opacity-80 disabled:cursor-not-allowed"
                >
                  Copy AI prompt
                </button>
              </div>
            )} */}
            <div
              style={{
                width: "100%",
                borderRadius: "16px",
                display: "flex",
                gap: "6px",
                alignItems: "center",
                position: "relative",
              }}
              id="usetoolbar_input_blurred"
            >
              {uploadedImages.length > 0 && (
                <div
                  className="absolute z-[11] bottom-full left-0 right-0 mb-1.5 rounded-[14.6px] bg-gray-300 py-[7px]"
                  style={{ boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)" }}
                >
                  {/* Left scroll button */}
                  <AnimatePresence>
                    {canScrollLeft && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        type="button"
                        onClick={scrollImagesLeft}
                        className="absolute left-[7px] top-1/2 -translate-y-1/2 z-10 w-[24px] h-[24px] bg-gray-400/[0.32] backdrop-blur-[24px] rounded-[10px] flex items-center justify-center cursor-pointer transition-colors"
                      >
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <path
                            d="M5 1L2 4L5 7"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </motion.button>
                    )}
                  </AnimatePresence>

                  <div
                    ref={imageScrollRef}
                    className="flex gap-[6px] overflow-scroll no-scrollbar"
                  >
                    {uploadedImages.map((image, i) => (
                      <div
                        key={i}
                        className={`${
                          i === 0
                            ? "ml-[7px]"
                            : i === uploadedImages.length - 1
                              ? "mr-[7px]"
                              : ""
                        } border-[1px] w-[65px] h-[65px] shrink-0 border-white/[0.07] rounded-[10px] overflow-hidden relative inline-block cursor-pointer`}
                        onClick={() => handleOpenMediaSlider(uploadedImages, i)}
                      >
                        <img
                          src={image}
                          alt={`Upload ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {uploadedImageUrls[i] && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage(i);
                            }}
                            className="absolute top-[2.5px] right-[2.5px] w-4 h-4 bg-[#1d1d1d]/[0.5] rounded-full flex items-center justify-center cursor-pointer border backdrop-blur-md"
                          >
                            <img src={TimesSmMedia} />
                            {/* <img src={TimesRounded} */}
                            {/* <X size={14} color="#ffffff" /> */}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Right scroll button */}
                  <AnimatePresence>
                    {canScrollRight && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        type="button"
                        onClick={scrollImagesRight}
                        className="absolute right-[7px] top-1/2 -translate-y-1/2 z-10 w-[24px] h-[24px] bg-gray-400/[0.32] backdrop-blur-[24px] rounded-[10px] flex items-center justify-center cursor-pointer transition-colors"
                      >
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <path
                            d="M3 1L6 4L3 7"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              )}
              {currentTab === "comments" && (
                <div className="group bg-gray-300 rounded-[14.5px] p-[0.5px] shrink-0">
                  <button
                    type="button"
                    className="h-[36px] p-0 w-[36px] flex items-center justify-center outline-none border-[0.5px] border-gray-700 bg-transparent cursor-pointer flex-shrink-0 rounded-[14px]"
                    onClick={handleMediaButtonClick}
                    onMouseEnter={(e) => {
                      const svg = e.currentTarget.querySelector("path");
                      if (svg) svg.setAttribute("fill", "#ffffff");
                    }}
                    onMouseLeave={(e) => {
                      const svg = e.currentTarget.querySelector("path");
                      if (svg) svg.setAttribute("fill", "#545454");
                    }}
                  >
                    <div className="w-[32px] h-[32px] bg-transparent group-hover:bg-white/[0.08] transition-colors rounded-[12px] flex items-center justify-center">
                      <PlusMedia color={"#545454"} />
                    </div>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              )}
              <div className="bg-gray-300 rounded-[14.5px] p-[0.5px] flex-1">
                <form
                  // style={{
                  //   backgroundColor: "rgba(29, 29, 29, 0.8)",
                  // }}
                  className="no-scrollbar_usetoolbar_widget border-[0.7px] border-gray-700 max-h-[500px] min-h-[36px] items-center rounded-[14px] flex relative w-full"
                >
                  {/* <form
                style={{
                  backgroundColor: "rgba(29, 29, 29, 0.8)",
                }}
                className="no-scrollbar_usetoolbar_widget backdrop-blur-[10px] border-[0.5px] border-white/35 overflow-scroll max-h-[500px] py-1.5 flex-1 items-center rounded-[15px] flex relative w-full"
              > */}
                  <div className="flex items-center w-full">
                    {!isRecording && (
                      <textarea
                        placeholder={currentTab === "ai" ? "Ask AI" : "Comment"}
                        id="add_comment_textarea"
                        className="usetoolbar_input_styles placeholder:text-gray-800 flex-1 h-[35px] leading-[21px] bg-transparent outline-none text-sm resize-none overflow-hidden px-[14.5px] py-[7px] border-transparent"
                        autoFocus
                        ref={textareaRef}
                        value={comment}
                        onChange={(e: any) => {
                          setComment(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleCommentSubmit();
                          }
                        }}
                      />
                    )}

                    <div
                      className={`flex justify-end items-center ${
                        isRecording ? "gap-3" : "gap-2"
                      } pr-[6.5px] ${
                        !isRecording ? "flex-shrink-0" : "w-full"
                      }`}
                    >
                      {/* Recorded audio player */}
                      {audioURL && !isRecording ? (
                        <div className="flex items-center gap-2 flex-1 py-2">
                          <div className="flex-1">
                            <CommentAudioPlayer
                              audioUrl={audioURL}
                              duration={recordingDuration}
                            />
                          </div>
                        </div>
                      ) : isRecording ? (
                        /* Voice recording UI */
                        <>
                          <button
                            type="button"
                            className="outline-none border-0 bg-transparent cursor-pointer p-0 pl-[6.5px] flex items-center"
                            onClick={handleCancelRecording}
                          >
                            <img src={TimesRounded} />
                          </button>
                          <div className="flex-1">
                            <AudioWaveform
                              analyserNode={analyserNode}
                              isRecording={true}
                              color="#ffffff"
                              barCount={100}
                              barWidth={2}
                              barGap={2}
                              height={21}
                            />
                          </div>
                          <div className="text-[#878787] text-xs font-medium min-w-[35px] text-center">
                            {(() => {
                              const mins = Math.floor(recordingTime / 60);
                              const secs = recordingTime % 60;
                              return `${mins}:${secs
                                .toString()
                                .padStart(2, "0")}`;
                            })()}
                          </div>
                        </>
                      ) : null}

                      {/* Microphone button - show only when no text, not recording, no images, and on comments tab */}
                      {!isRecording &&
                        !audioURL &&
                        !comment.trim() &&
                        uploadedImages.length === 0 &&
                        currentTab === "comments" && (
                          <button
                            type="button"
                            className="outline-none border-0 bg-transparent cursor-pointer flex items-center w-[23px] h-[23px]"
                            onClick={handleMicClick}
                            onMouseEnter={() => setIsMicHovered(true)}
                            onMouseLeave={() => setIsMicHovered(false)}
                          >
                            <MicrophoneGrey
                              color={isMicHovered ? "white" : "#878787"}
                            />
                          </button>
                        )}

                      {/* Send/Stop button */}
                      <button
                        type="button"
                        className="outline-none border-0 bg-transparent w-6 h-6 transition-opacity"
                        style={{
                          opacity: 1,
                          cursor:
                            isRecording ||
                            (!isUploadingImages &&
                              (comment.trim() || uploadedImageUrls.length > 0))
                              ? "pointer"
                              : "not-allowed",
                        }}
                        disabled={
                          !isRecording &&
                          (isUploadingImages ||
                            (!comment.trim() && uploadedImageUrls.length === 0))
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          if (isRecording) {
                            stopRecording();
                          } else {
                            handleCommentSubmit();
                          }
                        }}
                      >
                        <RoundedArrow
                          opacity={1}
                          color={
                            isRecording ||
                            (!isUploadingImages &&
                              (comment.trim() || uploadedImageUrls.length > 0))
                              ? "white"
                              : "#878787"
                          }
                        />
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          {/* )} */}
        </div>

        {/* Resizable divider - improved for better usability */}
        {!report.noTechnicalData && (
          <div
            className="hidden h-[8px] cursor-row-resize lg:flex items-center justify-center relative group"
            onMouseDown={() => setIsResizing(true)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              cursor: isResizing
                ? "row-resize"
                : isHovering
                  ? "row-resize"
                  : "default",
            }}
          >
            <AnimatePresence>
              {isHovering && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div
                    className={`
            w-16 h-[2px] bg-gray-900 rounded-full transition-all duration-200
           
          `}
                  ></div>
                </motion.div>
              )}
            </AnimatePresence>
            <div
              className="absolute inset-0 z-10"
              style={{ cursor: "row-resize" }}
            ></div>
          </div>
        )}

        {/* Second section with dynamic height based on divider position */}
        {!report.noTechnicalData && (
          <div
            className="w-full bg-gray-100 rounded-[24px] overflow-hidden flex flex-col"
            style={{
              height: width > 1023 ? `${100 - dividerPosition}%` : "50%",
            }}
          >
            <div className="border-b border-gray-300 p-[12px] flex-shrink-0 flex justify-between">
              <nav className="flex overflow-x-auto gap-[2px] no-scrollbar">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1.5 text-[14px] hover:bg-gray-400 font-medium leading-[20px] rounded-[12px] transition-all ${
                      activeTab === tab ? "bg-gray-300" : "bg-transparent"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
              <div className="relative" ref={threeDotsRef}>
                <button
                  onClick={() =>
                    setIsThreeDotsDropdownOpen(!isThreeDotsDropdownOpen)
                  }
                  className={`w-[32px] h-[32px] flex items-center justify-center border-[1px] rounded-[12px] transition-all border-gray-300 ${
                    isThreeDotsDropdownOpen
                      ? "bg-gray-200 hover:bg-gray-300"
                      : "bg-transparent hover:bg-gray-200"
                  }`}
                >
                  <img src={ThreeDots} />
                </button>
                <AnimatePresence>
                  {isThreeDotsDropdownOpen && (
                    <ThreeDotsDropdown
                      setIsThreeDotsDropdownOpen={setIsThreeDotsDropdownOpen}
                      report={report}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col">
              {activeTab === "Info" && (
                <div className="p-[12px] overflow-y-auto h-full flex flex-col">
                  <div className="flex flex-col gap-[2px]">
                    {!report?.isFromRecordingLink && (
                      <div className="even:bg-gray-300 h-[32px] flex px-3 py-1.5 rounded-[12px] w-full items-center justify-between">
                        <label className="text-sm font-medium leading-[20px] text-gray-800">
                          URL
                        </label>
                        <div className="pl-[6px] truncate text-sm font-medium leading-[20px] text-white flex items-center gap-[6px]">
                          <a
                            target="_blank"
                            className="truncate"
                            href={report.info?.url}
                          >
                            {report.info?.url || "Unknown"}
                          </a>
                          <img src={Link} />
                        </div>
                      </div>
                    )}

                    <div className="even:bg-gray-300 px-3 h-[32px] py-1.5 rounded-[12px] flex w-full items-center justify-between">
                      <label className="text-sm font-medium text-gray-800">
                        Timestamp
                      </label>
                      <div className="truncate text-sm font-medium leading-[20px] text-white flex items-center gap-[6px]">
                        <p className="pl-[6px] truncate">
                          {formatDate(report.createdAt || report.updatedAt)}
                        </p>
                        <img src={Timestamp} />
                      </div>
                    </div>

                    <div className="even:bg-gray-300 px-3 h-[32px] py-1.5 rounded-[12px] flex w-full items-center justify-between">
                      <label className="text-sm font-medium text-gray-800">
                        OS
                      </label>
                      <div className="text-sm font-medium leading-[20px] text-white flex items-center gap-[6px]">
                        {report.info?.os || "Unknown"}
                        {report.info?.os?.toLowerCase().includes("mac") && (
                          <img src={Apple} />
                        )}
                      </div>
                    </div>

                    <div className="even:bg-gray-300 px-3 h-[32px] py-1.5 rounded-[12px] flex w-full items-center justify-between">
                      <label className="text-sm font-medium text-gray-800">
                        Browser
                      </label>
                      <div className="text-sm font-medium leading-[20px] text-white flex items-center gap-[6px]">
                        {report.info?.browser || "Unknown"}
                        {report.info?.browser
                          ?.toLowerCase()
                          .includes("chrome") && <img src={Chrome} />}
                      </div>
                    </div>

                    <div className="even:bg-gray-300 px-3 h-[32px] py-1.5 rounded-[12px] flex w-full items-center justify-between">
                      <label className="text-sm font-medium text-gray-800">
                        Window size
                      </label>
                      <div className="text-sm font-medium leading-[20px] text-white flex items-center gap-[6px]">
                        {report.info?.windowSize?.width &&
                        report.info?.windowSize?.height
                          ? `${report.info?.windowSize.width}x${report.info?.windowSize.height}`
                          : "Unknown"}
                        <img src={Desktop} />
                      </div>
                    </div>

                    <div className="even:bg-gray-300 px-3 h-[32px] py-1.5 rounded-[12px] flex w-full items-center justify-between">
                      <label className="text-sm font-medium text-gray-800">
                        Country
                      </label>
                      <div className="text-sm font-medium leading-[20px] text-white flex items-center gap-[6px]">
                        {report.info?.country || "Unknown"}
                        {report.info?.country === "Estonia" && (
                          <span className="">🇪🇪</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto pt-3 flex items-stretch">
                    <button
                      onClick={() => setIsMCPModalOpen(true)}
                      style={{ borderRadius: "14px 0 0 14px" }}
                      className="flex-1 flex items-center justify-center px-4 py-3 bg-gray-300 hover:bg-gray-400 transition-all"
                    >
                      <span className="text-[14px] font-medium leading-[20px] text-white">
                        Fix issue with Claude Code | Toolbar MCP
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        const link = `https://app.usetool.bar/media?id=${report?._id}`;
                        navigator.clipboard.writeText(link);
                        showCustomToast("Link copied", "check");
                      }}
                      style={{ borderRadius: "0 14px 14px 0" }}
                      className="flex border-l-[1px] border-white/[0.08] items-center justify-center p-3 bg-gray-300 hover:bg-gray-400 transition-all"
                    >
                      <img src={CopySm} alt="Copy" />
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "Console" && (
                <div className="flex flex-col h-full overflow-hidden">
                  {report?.consoleLogs?.length > 0 && (
                    <div className="relative pl-[50px] py-[12px] pr-[24px] flex gap-[6px] flex-shrink-0">
                      <img
                        src={GreySearch}
                        className="absolute top-[50%] translate-y-[-50%] left-[24px] pointer-events-none"
                      />
                      <input
                        type="text"
                        placeholder="Filter"
                        value={consoleSearchTerm}
                        onChange={(e) => setConsoleSearchTerm(e.target.value)}
                        className="w-full text-[14px] leading-[20px] font-medium placeholder:text-gray-800 bg-transparent text-sm focus:outline-none"
                      />
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto no-scrollbar">
                    {filteredConsoleLogs.length > 0 ? (
                      <div className="flex flex-col gap-[1px] geist">
                        {filteredConsoleLogs.map((log, index) => (
                          <ConsoleLogItem key={index} log={log} />
                        ))}
                      </div>
                    ) : (
                      <div className="flex h-full w-[240px] mx-auto flex-col items-center justify-center">
                        <img src={ConsoleLogs} />
                        <div className="text-center">
                          <p className="text-[16px] font-medium text-white leading-[24px]">
                            No Console Logs
                          </p>
                          <p className="text-[14px] font-medium leading-[20px] mt-1 text-gray-800">
                            Console’s quiet. Is this a bug or a feature?
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "Network" && (
                <div className="flex flex-col h-full overflow-hidden">
                  {report?.networkLogs?.length > 0 && (
                    <div className="relative pl-[50px] py-[12px] pr-[24px] flex gap-[6px] flex-shrink-0">
                      <img
                        src={GreySearch}
                        className="absolute top-[50%] translate-y-[-50%] left-[24px] pointer-events-none"
                      />
                      <input
                        type="text"
                        placeholder="Filter"
                        value={networkSearchTerm}
                        onChange={(e) => setNetworkSearchTerm(e.target.value)}
                        className="w-full text-[14px] leading-[20px] font-medium placeholder:text-gray-800 bg-transparent text-sm focus:outline-none"
                      />
                    </div>
                  )}
                  <div className="flex-1 overflow-y-auto no-scrollbar">
                    {filteredNetworkLogs?.length > 0 ? (
                      <ResizableTable rows={filteredNetworkLogs} />
                    ) : (
                      <div className="flex h-full w-[240px] mx-auto flex-col items-center justify-center">
                        <img src={NetworkLogs} />
                        <div className="text-center">
                          <p className="text-[16px] font-medium text-white leading-[24px]">
                            No Network Data
                          </p>
                          <p className="text-[14px] font-medium leading-[20px] mt-1 text-gray-800">
                            Looks like the network decided to take a coffee
                            break.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "Actions" && (
                <div className="flex flex-col h-full overflow-hidden">
                  {report?.userActions?.length > 0 && (
                    <div className="relative pl-[50px] py-[12px] pr-[24px] flex gap-[6px] flex-shrink-0">
                      <img
                        src={GreySearch}
                        className="absolute top-[50%] translate-y-[-50%] left-[24px] pointer-events-none"
                      />
                      <input
                        type="text"
                        placeholder="Filter"
                        value={actionSearchTerm}
                        onChange={(e) => setActionSearchTerm(e.target.value)}
                        className="w-full text-[14px] leading-[20px] font-medium placeholder:text-gray-800 bg-transparent text-sm focus:outline-none"
                      />
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto no-scrollbar">
                    {filteredActions.length > 0 ? (
                      <div className="flex flex-col gap-[1px] geist">
                        {filteredActions.map((action: any, index: number) => {
                          return (
                            <div
                              key={action._id || index}
                              className={`flex items-start gap-2 px-6 py-2 ${
                                action.type === "navigate"
                                  ? "bg-blue/[0.08]"
                                  : "bg-transparent"
                              }`}
                            >
                              {/* Time */}
                              <div className="text-xs leading-[16px] text-gray-800 shrink-0">
                                {action.timeOffset}
                              </div>

                              {action.type === "navigate" && (
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <div className="w-[6px] h-[6px] rounded-[50%] bg-blue" />
                                </div>
                              )}

                              {action.type === "click" && <img src={Click} />}

                              {action.type === "screenshot" && (
                                <img src={Screenshot} />
                              )}

                              {action.type === "type" && (
                                <img src={TypingArrow} />
                              )}

                              {(action.type === "recording_start" ||
                                action.type === "recording_stop" ||
                                action.type === "dom_replay_stop") && (
                                <img src={Recording} />
                              )}

                              {/* Content */}
                              <div className="flex flex-col text-xs text-gray-900 w-full">
                                <div className="flex items-start gap-2">
                                  <span
                                    className={`font-medium 
                              ${
                                action.type === "navigate"
                                  ? "text-blue"
                                  : "text-white"
                              }`}
                                  >
                                    {action.type === "navigate"
                                      ? "Navigated to"
                                      : action.type === "click"
                                        ? "Clicked"
                                        : action.type === "screenshot"
                                          ? "Screenshot taken"
                                          : action.type === "recording_start"
                                            ? "Recording started"
                                            : action.type === "recording_stop"
                                              ? "Recording stopped"
                                              : action.type === "type"
                                                ? "Typed"
                                                : action.type ===
                                                    "dom_replay_stop"
                                                  ? "Replay stopped"
                                                  : ""}
                                  </span>
                                  {action.type === "type" && action?.value && (
                                    <div className="h-[16px] rounded-[6px] bg-gray-400 border-[#fff]/[0.08] text-[10px] leading-[16px] flex items-center justify-center px-1 border-[1px] font-medium backdrop-blur-[10px]">
                                      {action.value}
                                    </div>
                                  )}
                                  {action.type === "type" &&
                                    action?.target?.selector && (
                                      <>
                                        in <div>{action.target.selector}</div>
                                      </>
                                    )}
                                  {action.type === "navigate" && (
                                    <span className="text-gray-800 truncate max-w-[250px]">
                                      {action.url}
                                    </span>
                                  )}
                                  {action.type === "click" &&
                                    action?.target &&
                                    action.target?.selector && (
                                      <span className="text-white w-full">
                                        {action.target.selector}
                                      </span>
                                    )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex h-full w-[240px] mx-auto flex-col items-center justify-center">
                        <img src={UserActions} />
                        <div className="text-center">
                          <p className="text-[16px] font-medium text-white leading-[24px]">
                            No User Actions
                          </p>
                          <p className="text-[14px] font-medium leading-[20px] mt-1 text-gray-800">
                            The user probably didn't really have anything to
                            say...
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "Sentry" && (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="flex-1 overflow-y-auto no-scrollbar">
                    {(report?.sentryErrors?.length ?? 0) > 0 ? (
                      <div className="flex flex-col gap-[1px] geist">
                        {report.sentryErrors!.map((error, index) => (
                          <div
                            key={error.eventId || index}
                            className="flex flex-col gap-1 px-6 py-3 border-b border-gray-300"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-[6px] ${
                                  error.level === "error"
                                    ? "bg-red-500/20 text-red-400"
                                    : error.level === "warning"
                                      ? "bg-yellow-500/20 text-yellow-400"
                                      : "bg-blue/20 text-blue"
                                }`}
                              >
                                {error.level}
                              </span>
                              <span className="text-xs text-gray-800">
                                {new Date(error.dateCreated).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-[13px] font-medium text-white leading-[18px]">
                              {error.title}
                            </p>
                            {error.culprit && (
                              <p className="text-xs text-gray-800 truncate">
                                {error.culprit}
                              </p>
                            )}
                            {error.message && error.message !== error.title && (
                              <p className="text-xs text-gray-800">
                                {error.message}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex h-full w-[240px] mx-auto flex-col items-center justify-center">
                        <div className="text-center">
                          <p className="text-[16px] font-medium text-white leading-[24px]">
                            No Sentry Errors
                          </p>
                          <p className="text-[14px] font-medium leading-[20px] mt-1 text-gray-800">
                            No errors found around the time of this report.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MediaSliderFull for viewing images */}
      {showMediaSlider && (
        <MediaSliderFull
          slides={mediaSliderImages}
          currentIndex={mediaSliderStartIndex}
          onClose={handleCloseMediaSlider}
          onIndexChange={setCurrentMediaIndex}
          mediaType="image"
          options={{ loop: false }}
          onOpenNewImages={handleOpenMediaSlider}
        />
      )}

      <MCPModal
        isOpen={isMCPModalOpen}
        onClose={() => setIsMCPModalOpen(false)}
      />
    </>
  );
};

export default ReportSidebar;
